import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import moment from 'moment';
import { GridApi, IServerSideGetRowsParams } from '@ag-grid-community/core';
import { Injector } from '@angular/core';

import { DatacenterSelectionService } from '../../../services/datacenter-selection.service';
import { OrderBy, OrderByDirection } from '../../../utils/constant';
import { PaginatedResult } from '../../../models/paginated-result';
import { formatTextDimension } from '../../../utils/number.format';
import { FilterDateFormatType, DATE_FORMAT_PATTERN_DD_MM_YYYY,
	WEEKYEAR_FORMAT_PATTERN } from '../../../models/filter-formats';
import { CommonGridOptions } from '../../grid-options';
import { ColDefWithFields, ColumnType } from '../../column-type';
import { Organization } from '../../../../organization/models/organization';
import { OrganizationService } from '../../../../organization/services/organization.service';
import { Strategy } from '../strategy';
import { FilterModel } from '../../../models/ag-grid';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';

export class StrategyOrganization implements Strategy<Organization> {
	organizationService: OrganizationService;
	selectionService: DatacenterSelectionService;
	defaultOrderBy = new OrderBy<keyof Organization>('name', OrderByDirection.DESC);
	hasTotal = false;
	aggregateFields = [];

	constructor(injector: Injector) {
		this.organizationService = injector.get<OrganizationService>(OrganizationService);
		this.selectionService = injector.get<DatacenterSelectionService>(DatacenterSelectionService);
	}

	setTotal(_api?: GridApi, _filter?: string): Subscription {
		throw new Error('Method not implemented.');
	}

	async getAllDataSelected(
		currentColumns: string[],
		filter: string | undefined,
		orderBy: OrderBy<keyof Organization>[],
	) {
		return this.getAllData(orderBy, currentColumns, filter)
			.then((organizations: PaginatedResult<Organization>) => this.removeDeselectedRows(organizations));
	}

	async getAllData(
		orderBy: OrderBy<keyof Organization>[],
		currentColumns: string[],
		filter: string | undefined
	) {
		return firstValueFrom(this.organizationService.getAllOrganizations(
			<(keyof Organization)[]> currentColumns,
			filter,
			0,
			60_000,
			orderBy,
			false
		));
	}

	formatCellByType(columnDef: ColDefWithFields<Organization>, data: Organization) {
		const field = columnDef.field || '';
		const fields = columnDef.fields || [];
		const { columnType } = columnDef;
		switch (columnType) {
		case ColumnType.DIMENSION_COLUMN:
			return formatTextDimension(
				<string> data[<keyof Organization> fields[0]],
				<string> data[<keyof Organization> fields[1]],
				<string> data[<keyof Organization> fields[2]]
			);
		case ColumnType.ORDER_NUMBER_COLUMN:
		{
			let orderNumber = '';
			if (<string> data[<keyof Organization> fields[0]]) {
				orderNumber = <string> data[<keyof Organization> fields[0]];
			}
			if (<string> data[<keyof Organization> fields[0]] && <string> data[<keyof Organization> fields[1]]) {
				orderNumber += `/${ <string> data[<keyof Organization> fields[1]] }`;
			}
			return orderNumber;
		}
		case ColumnType.NUMBER_COLUMN:
		{
			const value = <number | undefined >data[<keyof Organization> field];
			return value === undefined ? '' : String(value);
		}
		case ColumnType.TEXT_COLUMN:
			return field.includes('.')
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
				? this.getNestedProperty(data, <string>columnDef.filterParams.organizationFields[1])
				: <string> data[<keyof Organization> columnDef.colId];
		case ColumnType.DATE_COLUMN:
			if (data[<keyof Organization> field]) {
				const dateformat = (<FilterDateFormatType> columnDef.cellRendererParams).outputFormat === 'week'
					? WEEKYEAR_FORMAT_PATTERN : DATE_FORMAT_PATTERN_DD_MM_YYYY;
				return moment.utc(<string> data[<keyof Organization> field]).format(dateformat);
			}
			return '';
		case ColumnType.TRANSLATED_COLUMN:
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			return this.getNestedProperty(data, <string>columnDef.filterParams.organizationFields[1]);
		default:
			throw new Error('unknown filter type');
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any,
	getNestedProperty(obj: any, path: string) {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
		return <string>path.split('.').reduce((acc, part) => acc?.[part], obj);
	}

	getCustomGridOptions(defaultGridOptions: CommonGridOptions) {
		return new CommonGridOptions<Organization>({
			...defaultGridOptions,
			getRowId: ({ data }: { data: Organization }) => String(data.id)
		});
	}

	getDataFromGrid(
		orderBy: OrderBy<keyof Organization>[],
		currentColumns: (keyof Organization)[],
		filter: string | undefined,
		params: IServerSideGetRowsParams<Organization>,
		rowsCountSubject: BehaviorSubject<number | undefined>
	) {
		const currentColumnsWithId = this.addIdColumn(currentColumns);
		return this.organizationService.getAllOrganizations(
			currentColumnsWithId,
			filter,
			params.request.startRow ?? 0,
			250,
			orderBy,
			false
		).subscribe({
			next: res => {
				const lastRow = params.request.endRow === undefined || res.count <= params.request.endRow
					? res.count
					: -1;
				params.success({ rowData: res.results, rowCount: lastRow });
				rowsCountSubject.next(res.count);
			},
			error: (err: unknown) => {
				console.error('error requesting organization datacenter', err);
				rowsCountSubject.next(undefined);
				params.fail();
			}
		});
	}

	addIdColumn(currentColumns: (keyof Organization)[]) {
		if (!currentColumns.includes('id')) {
			currentColumns.push('id');
		}
		return currentColumns;
	}

	createAggregateCustomViewFilter(_view: UserDatacenterView): string {
		throw new Error('Method not implemented.');
	}

	createAggregateQuickSearchFilter(
		_currentSelectedView: UserDatacenterView,
		_api?: GridApi,
		_filterModel?: FilterModel
	): string {
		throw new Error('Method not implemented.');
	}

	private removeDeselectedRows(organizations: PaginatedResult<Organization>) {
		return organizations.results.filter(batch => {
			const rowDeselected = this.selectionService.findDeselectedNode(String(batch.id));
			return !rowDeselected;
		});
	}
}
